import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useAuth } from "@/contexts/AuthContext";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorDisplay } from "@/components/ErrorDisplay";
import styles from "./Auth0Callback.module.css";


/**
 * Auth0 Callback Component
 * Handles the redirect from Auth0 after authentication
 */
export const Auth0Callback: React.FC = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: auth0Loading, error: auth0Error, getAccessTokenSilently, user } = useAuth0();
  const { login, showInvalidCredentialsModal } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const [stages, setStages] = useState<Array<{ label: string; completed: boolean }>>([
    { label: "Verifying identity", completed: false },
    { label: "Fetching user accounts", completed: false },
    { label: "Loading permissions", completed: false },
    { label: "Setting up session", completed: false },
  ]);
  const hasProcessedRef = useRef(false);

  const updateStage = (index: number) => {
    setStages(prev => prev.map((stage, i) => 
      i === index ? { ...stage, completed: true } : stage
    ));
  };

  useEffect(() => {
    const handleCallback = async () => {
      // Prevent double execution - check and set immediately
      if (hasProcessedRef.current) {
        return;
      }
      hasProcessedRef.current = true;

      // Wait for Auth0 to finish loading
      if (auth0Loading) {
        hasProcessedRef.current = false; // Reset if still loading
        return;
      }

      // Handle Auth0 errors
      if (auth0Error) {
        console.error("❌ Auth0Callback: Auth0 error:", auth0Error);
        setError(`Authentication failed: ${auth0Error.message}`);
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      // Check if authenticated
      if (!isAuthenticated) {
        console.error("❌ Auth0Callback: Not authenticated");
        setError("Authentication failed. Please try again.");
        setTimeout(() => navigate('/login'), 2000);
        return;
      }

      updateStage(0); // Stage 1: Authenticating complete

      // Set a timeout to prevent hanging indefinitely
      let timeoutTriggered = false;
      const timeoutId = setTimeout(() => {
        timeoutTriggered = true;
        console.error("❌ Auth0Callback: Timeout - process took too long");
        setError("Authentication timeout. Please try again.");
        setTimeout(() => navigate('/login'), 2000);
      }, 30000); // 30 second timeout

      try {
        // Get access token
        const token = await getAccessTokenSilently();

        // Store token for pilets
        localStorage.setItem("authToken", token);
        sessionStorage.setItem("authToken", token);

        // Store user info for pilets
        if (user) {
          localStorage.setItem("currentUser", JSON.stringify({
            id: user.sub,
            email: user.email,
            name: user.name,
            firstName: user.given_name,
            lastName: user.family_name,
            photoUrl: user.picture,
          }));
        }

        // Fetch account types to determine available contexts.
        // Any failure/empty response/zero consumerAccountTypes -> show
        // "Invalid Username or Password" modal and logout (same handling
        // as the blocked-PARTNER-context flow in AuthContext).
        const apiServiceModule = await import("@/services/apiService");
        const apiService = apiServiceModule.default;

        let consumerAccountTypes: string[] = [];
        let partnerAccountTypes: string[] = [];
        let accountTypesFailed = false;
        try {
          const accountTypesData = await apiService.getAccountTypes();
          consumerAccountTypes = accountTypesData?.data?.consumerAccountTypes || [];
          partnerAccountTypes = accountTypesData?.data?.partnerAccountTypes || [];
          if (!accountTypesData?.data) {
            accountTypesFailed = true;
          }
        } catch (accountTypesErr) {
          console.error("❌ Auth0Callback: getAccountTypes failed:", accountTypesErr);
          accountTypesFailed = true;
        }

        // Only proceed when we have at least one consumer account type.
        // All other conditions (API failure, empty response, zero consumer
        // account types) result in logout + invalid-credentials modal.
        if (accountTypesFailed || consumerAccountTypes.length === 0) {
          clearTimeout(timeoutId);
          if (timeoutTriggered) {
            return;
          }
          showInvalidCredentialsModal();
          return;
        }

    // Build lightweight contexts from account types (no role/permissions yet)
        const contexts: Array<{
          context: string;
          contextDisplayName: string;          
        }> = [];

        // Each consumer account type becomes its own context
        const consumerDisplayNames: Record<string, string> = {
        RESIDENTIAL: "Residential",
        COMMERCIAL: "Commercial",
        };
        for (const accountType of consumerAccountTypes) {
        contexts.push({
          context: accountType,
          contextDisplayName:
          consumerDisplayNames[accountType] || accountType,
        });
        }
 
        // Partner account types map to PARTNER context
        if (partnerAccountTypes.length > 0) {
        contexts.push({
          context: "PARTNER",
          contextDisplayName: "Partner", 
        });
        }

        // Check if user has any contexts
        if (contexts.length === 0) {
          console.error("❌ No contexts found for user");
          setError("No accounts found. Please contact support.");
          setTimeout(() => navigate('/login'), 3000);
          return;
        }

        updateStage(1); // Stage 2: Fetching accounts complete

         // Build user data (permissions will be loaded after context selection)
        const userData = {
        id: user?.sub || "",
        email: user?.email || "",
        name: user?.name || "",
        contexts: contexts.map((ctx) => ({
          context: ctx.context,
          contextDisplayName: ctx.contextDisplayName,
        })),
        };
        // Login with user data — AuthContext handles navigation
        await login(userData);
        updateStage(2); // Stage 3: Session setup complete
        
        // Clear the timeout since we succeeded
        clearTimeout(timeoutId);
        
        // Don't navigate if timeout already triggered
        if (timeoutTriggered) {
          return;
        }        
        
      } catch (err) {
        clearTimeout(timeoutId); // Clear timeout on error
        
        // Don't show error if timeout already handled it
        if (timeoutTriggered) {
          return;
        }
        
        console.error("❌ Auth0Callback: Error processing callback:", err);
        setError(err instanceof Error ? err.message : "Authentication failed");
        setTimeout(() => navigate('/login'), 2000);
      }
    };

    handleCallback();
  }, [isAuthenticated, auth0Loading, auth0Error, getAccessTokenSilently, user, login, navigate, showInvalidCredentialsModal]);

  // Show error if present
  if (error) {
    return (
      <div className={styles.callbackContainer}>
        <ErrorDisplay message={error} />
      </div>
    );
  }

  // Calculate progress percentage
  const completedStages = stages.filter(s => s.completed).length;
  const progressPercent = (completedStages / stages.length) * 100;

  // Show loading with progress stages
  return (
    <div className={styles.callbackContainer}>
      <div className={styles.loadingCard}>
        <LoadingSpinner size="large" />
        
        <h2 style={{ marginTop: '24px', marginBottom: '8px', fontSize: '24px', fontWeight: '600' }}>
          Setting up your account
        </h2>
        
        <p style={{ color: '#666', marginBottom: '32px' }}>
          Please wait while we get everything ready...
        </p>

        {/* Progress Stages - Only show completed stages */}
        <div className={styles.stagesContainer}>
          {stages.map((stage, index) => 
            stage.completed ? (
              <div 
                key={index} 
                className={`${styles.stage} ${styles.completed}`}
              >
                <div className={styles.checkmark}>✓</div>
                <span className={styles.stageLabel}>{stage.label}</span>
              </div>
            ) : null
          )}
        </div>

        {/* Progress Bar */}
        <div className={styles.progressBar}>
          <div 
            className={styles.progressFill} 
            style={{ 
              width: `${progressPercent}%`,
              transition: 'width 0.5s ease'
            }}
          />
        </div>
      </div>
    </div>
  );
};